import { USER_TYPES } from 'constants/enum';
import { getCompany } from 'helpers/getCompanyVariables';

const isOneill = getCompany() === 'oneill';

// Zoho carrier ids for Neo Insurance, staging and prod (companyconfig.json only switches the api base url)
export const NEO_CARRIER_IDS = new Set([
  '4774415000317254385', // staging
  '4738157000318452001' // prod
]);

export const NEO_MAX_AGENTS_TO_CONTRACT = 100000;

// HubSpot form the agent completes after our request to finish the submission with Neo
export const NEO_SUBMISSION_FORM_URL =
  'https://405g5q.share-na2.hsforms.com/2a5vIJlHFRXWNVc7uEt1Qow';

// key is the exact string sent to Zoho, label is what the form shows, keep both in sync with the backend
export const NEO_QUESTIONS = {
  name: {
    key: 'Full Name',
    label: 'Full Name',
    placeholder: 'e.g. John Smith'
  },
  email: {
    key: 'Email',
    label: 'Email',
    placeholder: 'e.g. name@email.com'
  },
  phone: { key: 'Phone', label: 'Phone', placeholder: 'e.g. (555) 123-4567' },
  npn: { key: 'NPN', label: 'NPN', placeholder: 'e.g. 12345678' },
  independentAgent: {
    key: 'Are you an independent agent?',
    label: 'Are you an independent agent?'
  },
  advancingCommissions: {
    key: 'Are you interested in advancing for commissions?',
    label: 'Are you interested in advancing for commissions?'
  },
  agentsToContract: {
    key: 'How many agents are you looking to get contracted for these products?',
    label:
      'How many agents are you looking to get contracted for these products?',
    placeholder: 'e.g. 25'
  },
  interestedInLeads: {
    key: 'Are you interested in leads for these products?',
    label: 'Are you interested in leads for these products?'
  }
};

/**
 * true when the given carrier is Neo Insurance, which replaces the states/transfer flow with a contracting form
 * @param carrierData - carrier object built by the carrier factory
 */
export function isNeoCarrier(carrierData) {
  return isOneill && NEO_CARRIER_IDS.has(String(carrierData?.id ?? ''));
}

/**
 * true when the agent is an agency admin, who gets the admin-only Neo questions
 * @param user - Zoho agent record
 */
export function isNeoAdmin(user) {
  return user?.Position_at_Agency === USER_TYPES.ADMIN;
}

// zoho values can come back as numbers, the form state is always strings
function toFormValue(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * builds the Neo contracting form state, prefilled with the agent's Zoho data
 * @param user - Zoho agent record
 */
export function buildInitialNeoForm(user) {
  const fullName =
    user?.Full_Name ||
    [user?.First_Name, user?.Last_Name].filter(Boolean).join(' ');

  return {
    name: toFormValue(fullName),
    email: toFormValue(user?.Email),
    phone: toFormValue(user?.Phone),
    npn: toFormValue(user?.NPN),
    independentAgent: '',
    advancingCommissions: '',
    agentsToContract: '',
    interestedInLeads: ''
  };
}

/**
 * parses the "how many agents" answer, returning null when it is not a valid count
 * @param value - raw value typed into the field
 */
export function parseNeoAgentsToContract(value) {
  if (value === '' || value === null || value === undefined) return null;

  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  if (parsed < 1 || parsed > NEO_MAX_AGENTS_TO_CONTRACT) return null;

  return parsed;
}

export const NEO_FIELD_ERRORS = {
  lettersOnly: 'Only letters are allowed',
  numbersOnly: 'Only numbers are allowed',
  email: 'Enter a valid email address',
  agentsToContractRange: `Enter a number between 1 and ${NEO_MAX_AGENTS_TO_CONTRACT.toLocaleString(
    'en-US'
  )}`
};

// names can carry spaces, hyphens, apostrophes and periods, phones can carry separators
const LETTERS_ONLY_PATTERN = /^[a-zA-Z\s'.-]+$/;
const NUMBERS_ONLY_PATTERN = /^\d+$/;
const PHONE_PATTERN = /^[\d\s+()-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// the count is both digits only and bounded, so it needs the two checks in order
function getAgentsToContractError(value) {
  if (!value) return '';
  if (!NUMBERS_ONLY_PATTERN.test(value)) return NEO_FIELD_ERRORS.numbersOnly;
  if (parseNeoAgentsToContract(value) === null) {
    return NEO_FIELD_ERRORS.agentsToContractRange;
  }
  return '';
}

/**
 * validates the free text answers of the Neo form, empty answers are reported as valid here
 * @param form - Neo contracting form state
 * @param isAdmin - whether the agent is an agency admin, so the admin-only questions apply
 */
export function getNeoFieldErrors(form, isAdmin) {
  const name = form.name.trim();
  const email = form.email.trim();
  const phone = form.phone.trim();
  const npn = form.npn.trim();

  const errors = {
    name:
      name && !LETTERS_ONLY_PATTERN.test(name)
        ? NEO_FIELD_ERRORS.lettersOnly
        : '',
    email: email && !EMAIL_PATTERN.test(email) ? NEO_FIELD_ERRORS.email : '',
    phone:
      phone && !PHONE_PATTERN.test(phone) ? NEO_FIELD_ERRORS.numbersOnly : '',
    npn:
      npn && !NUMBERS_ONLY_PATTERN.test(npn)
        ? NEO_FIELD_ERRORS.numbersOnly
        : '',
    agentsToContract: ''
  };

  if (isAdmin) {
    errors.agentsToContract = getAgentsToContractError(
      form.agentsToContract.trim()
    );
  }

  return errors;
}

/**
 * true when every Neo question that applies to the agent has a valid answer
 * @param form - Neo contracting form state
 * @param isAdmin - whether the agent is an agency admin, so the admin-only questions apply
 */
export function isNeoFormValid(form, isAdmin) {
  const hasContactInfo = Boolean(
    form?.name?.trim() &&
      form?.email?.trim() &&
      form?.phone?.trim() &&
      form?.npn?.trim()
  );
  if (!hasContactInfo) return false;
  if (!form.independentAgent || !form.interestedInLeads) return false;

  const errors = getNeoFieldErrors(form, isAdmin);
  if (Object.values(errors).some(Boolean)) return false;
  if (!isAdmin) return true;

  return (
    Boolean(form.advancingCommissions) &&
    parseNeoAgentsToContract(form.agentsToContract) !== null
  );
}

/**
 * builds the ordered questions payload sent to Zoho for a Neo contracting request
 * @param form - Neo contracting form state
 * @param isAdmin - whether the agent is an agency admin, so the admin-only questions are included
 */
export function buildNeoQuestionsPayload(form, isAdmin) {
  const rows = [
    { question: NEO_QUESTIONS.name.key, answer: form.name.trim() },
    { question: NEO_QUESTIONS.email.key, answer: form.email.trim() },
    { question: NEO_QUESTIONS.phone.key, answer: form.phone.trim() },
    { question: NEO_QUESTIONS.npn.key, answer: form.npn.trim() },
    {
      question: NEO_QUESTIONS.independentAgent.key,
      answer: form.independentAgent
    }
  ];

  if (isAdmin) {
    rows.push(
      {
        question: NEO_QUESTIONS.advancingCommissions.key,
        answer: form.advancingCommissions
      },
      {
        question: NEO_QUESTIONS.agentsToContract.key,
        answer: form.agentsToContract.trim()
      }
    );
  }

  rows.push({
    question: NEO_QUESTIONS.interestedInLeads.key,
    answer: form.interestedInLeads
  });

  return rows;
}
